import { useEffect, useState } from 'react';
import useInterval from './use-interval.js';

const getAddress = (p) => {
    const { street, housenumber } = p;
    const road = street ? street + (housenumber ? ` ${housenumber}` : '') : '';
    return road ?? '';
};

const getName = (p, address) => {
    const { name } = p;
    return (name !== address ? name + (address ? ` - ${address}` : '') : name) ?? '';
};

const getCity = (p) => {
    const { locality, localadmin, region, county } = p;
    return locality ?? localadmin ?? region ?? county ?? '';
};

const getPhone = (p) => {
    if (!p) return '';
    const { osm } = p;
    return osm?.phone ?? '';
};

const fetchResults = async (value, setOptions) => {
    if (!value) return;
    const response = await fetch(
        `https://api.openrouteservice.org/geocode/autocomplete?api_key=${OPENROUTE_KEY}&layers=street,venue,address&text=${value}`
    );
    const values = await response.json();
    setOptions([
        ...new Set(
            values.features.map((f) => {
                const address = getAddress(f.properties);
                const name = getName(f.properties, address);
                const city = getCity(f.properties);
                const phone = getPhone(f.properties?.addendum);
                if (name)
                    return {
                        geometry: {
                            lat: f.geometry.coordinates[1],
                            lng: f.geometry.coordinates[0]
                        },
                        layer: f.properties.layer,
                        label: name + ' - ' + city,
                        name,
                        address,
                        city,
                        phone
                    };
            })
        )
    ]);
};

export function useAddressAutocomplete({ defaultQuery, defaultAddress, defaultCity, defaultPhone } = {}) {
    const [options, setOptions] = useState([]);
    const [query, setQuery] = useState(defaultQuery);
    const [oldQuery, setOldQuery] = useState(null);
    const [canSearch, setCanSearch] = useState(false);

    const [address, setAddress] = useState(defaultAddress);
    const [city, setCity] = useState(defaultCity);
    const [phone, setPhone] = useState(defaultPhone);
    const [geometry, setGeometry] = useState();

    useInterval(() => {
        setCanSearch(true);
    }, 500);

    useEffect(() => {
        if (!query) return setOptions([]);
        if (canSearch && query !== oldQuery) {
            fetchResults(query, setOptions);
            setOldQuery(query);
            setCanSearch(false);
        }
    }, [canSearch, query]);

    const handleChangeQuery = (v) => {
        setQuery(v);
    };

    const handleSetValue = (v) => {
        setQuery(v.label);
        setAddress(v.address);
        setCity(v.city);
        if (v.phone) setPhone(v.phone);
        if (v.geometry) setGeometry(v.geometry);
    };

    return { query, address, city, phone, geometry, handleChangeQuery, handleSetValue, options };
}
